import { FormData } from "../../../hooks/useCreateRecordForm";
import validator from "validator";

// Helper functions
const isValidEmail = (email: string): boolean => {
  return validator.isEmail(email);
};

const isValidPhoneNumber = (phone: string): boolean => {
  const phoneRegex = /^\d{10}$/;
  return phoneRegex.test(phone);
};

const isValidRFC = (rfc: string): boolean => {
  // Validate both personal and company RFC formats
  const personalRfcRegex = /^([A-ZÑ&]{4}([0-9]{2})(0[1-9]|1[0-2])(0[1-9]|1[0-9]|2[0-9]|3[0-1]))([A-Z\d]{2}([A]|[0-9]){1})?$/;
  const companyRfcRegex = /^[A-ZÑ&]{3}[0-9]{6}[A-Z0-9]{3}$/;
  return personalRfcRegex.test(rfc) || companyRfcRegex.test(rfc);
};

const isValidCURP = (curp: string): boolean => {
  const curpRegex = /^([A-Z][AEIOUX][A-Z]{2}[0-9]{2}(?:0[1-9]|1[012])(?:0[1-9]|[12]\d|3[01])[HM](?:AS|B[CS]|C[CLMSH]|D[FG]|G[TR]|HG|JC|M[CNS]|N[ETL]|OC|PL|Q[TR]|S[PLR]|T[CSL]|VZ|YN|ZS)[B-DF-HJ-NP-TV-Z]{3}[A-Z\d]{1})([\d]{1})$/;
  return curpRegex.test(curp);
};

// Step 1: Inquilino validation
export const isStep1Valid = (formData: FormData): boolean => {
  const hasRequiredFields =
    formData.tenantName.trim() !== "" &&
    formData.tenantFirstSurname.trim() !== "" &&
    formData.tenantEmail.trim() !== "" &&
    formData.tenantPhoneNumber.trim() !== "";

  if (!hasRequiredFields) return false;

  // Validate email format
  if (!isValidEmail(formData.tenantEmail)) return false;

  // Validate phone number
  if (!isValidPhoneNumber(formData.tenantPhoneNumber)) return false;

  // Validate optional RFC if provided
  if (formData.tenantRfc.trim() !== "" && !isValidRFC(formData.tenantRfc)) {
    return false;
  }

  // Validate optional CURP if provided
  if (formData.tenantCurp.trim() !== "" && !isValidCURP(formData.tenantCurp)) {
    return false;
  }

  return true;
};

// Step 2: Asesor validation
export const isStep2Valid = (formData: FormData): boolean => {
  const hasRequiredFields =
    formData.advisorName.trim() !== "" &&
    formData.advisorFirstSurname.trim() !== "" &&
    formData.advisorEmail.trim() !== "" &&
    formData.advisorPhoneNumber.trim() !== "";

  if (!hasRequiredFields) return false;

  // Validate email format
  if (!isValidEmail(formData.advisorEmail)) return false;

  // Validate phone number
  if (!isValidPhoneNumber(formData.advisorPhoneNumber)) return false;

  return true;
};

// Step 3: Fiador validation
export const isStep3Valid = (formData: FormData): boolean => {
  // If guarantor is not required, step is valid
  if (!formData.requiresGuarantor) {
    return true;
  }

  // If guarantor email is provided, validate format
  if (formData.guarantorEmail.trim() !== "" && !isValidEmail(formData.guarantorEmail)) {
    return false;
  }

  return true;
};

// Step 4: Convenio y Representante validation
export const isStep4Valid = (formData: FormData): boolean => {
  // User must answer the alternativeJustice question
  if (formData.alternativeJustice === null) {
    return false;
  }

  // If alternativeJustice is true, user must answer requiresOwnerRepresentative
  if (formData.alternativeJustice === true && formData.requiresOwnerRepresentative === null) {
    return false;
  }

  // If landlord email is provided, validate format
  if (formData.landlordEmail.trim() !== "" && !isValidEmail(formData.landlordEmail)) {
    return false;
  }

  return true;
};
